import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Pencil, Flag, Trash2, Receipt, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/KpiCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EditEntryDialog, RejectDialog, ReimbursementDialog } from "@/components/EntryDialogs";
import { AdminEntryCards } from "@/components/AdminEntryCards";
import { BulkReimbursementDialog } from "@/components/BulkReimbursementDialog";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtEur, fmtDate, monthStartISO, todayISO, kmlTone, stationColor, diffTone } from "@/lib/format";
import { useSort, SortTh } from "@/lib/sort";

export default function AdminControl() {
  const [stations, setStations] = useState([]);
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({ date_from: monthStartISO(), date_to: todayISO(), station_id: params.get("stazione") || "all", status: "all" });
  const [editing, setEditing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reimbursing, setReimbursing] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const { sorted, sort, toggle } = useSort(rows, {}, { key: "date", dir: "desc" });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const R = (p) => <S className="px-3 py-2 text-right" align="right" {...p} />;

  useEffect(() => { api.get("/stations").then((r) => setStations(r.data)); }, []);

  const load = useCallback(() => {
    const params = { date_from: f.date_from || undefined, date_to: f.date_to || undefined,
      station_id: f.station_id === "all" ? undefined : f.station_id, status: f.status === "all" ? undefined : f.status };
    api.get("/entries", { params }).then((r) => setRows(r.data));
  }, [f]);
  useEffect(load, [load]);

  const approve = async (r) => {
    try { await api.post(`/entries/${r.id}/approve`); toast.success("Record approvato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Eliminare definitivamente questo record?")) return;
    try { await api.delete(`/entries/${r.id}`); toast.success("Record eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const approveAll = async () => {
    const pend = rows.filter((r) => r.status === "Pending");
    if (!pend.length || !window.confirm(`Approvare ${pend.length} record in attesa?`)) return;
    await Promise.all(pend.map((r) => api.post(`/entries/${r.id}/approve`)));
    toast.success(`${pend.length} record approvati`);
    load();
  };

  const pendingCount = rows.filter((r) => r.status === "Pending").length;
  const selectStation = (id) => {
    setF({ ...f, station_id: id });
    setParams(id === "all" ? {} : { stazione: id });
  };
  const valid = rows.filter((r) => r.status !== "Rejected");
  const sum = (k) => valid.reduce((a, r) => a + (r[k] || 0), 0);
  const totLt = sum("fuel_liters");
  const both = valid.filter((r) => r.kilometers && r.fuel_liters);
  const kmRows = valid.filter((r) => r.kilometers);
  const bothKm = both.reduce((a, r) => a + r.kilometers, 0);
  const bothLt = both.reduce((a, r) => a + r.fuel_liters, 0);
  const kmTot = kmRows.reduce((a, r) => a + r.kilometers, 0);
  const kmCostNet = kmRows.reduce((a, r) => a + r.total_cost_net, 0);
  const current = stations.find((s) => s.id === f.station_id);
  const reimbRows = valid.filter((r) => r.reimbursement_amount != null);
  const totReimb = reimbRows.reduce((a, r) => a + r.reimbursement_amount, 0);
  const reimbNet = reimbRows.reduce((a, r) => a + r.total_cost_net, 0);
  const totDiff = totReimb - reimbNet;
  const summary = [
    ["Importo gasolio (IVA incl.)", fmtEur(sum("total_cost")), "sum-amount"],
    ["Gasolio netto IVA", fmtEur(sum("total_cost_net")), "sum-amount-net"],
    ["Rimborso Amazon (netto)", fmtEur(totReimb), "sum-reimbursement"],
    ["Differenza netta", reimbRows.length ? fmtEur(totDiff) : "—", "sum-difference", diffTone(reimbRows.length ? totDiff : null)],
    ["Km rimborsati", fmtNum(sum("reimbursed_km"), 0), "sum-reimbursed-km"],
    ["Litri", fmtNum(totLt, 0), "sum-liters"],
    ["Media km/l", fmtNum(bothLt ? bothKm / bothLt : null, 2), "sum-kml"],
    ["KM", fmtNum(sum("kilometers"), 0), "sum-km"],
    ["Media mezzi/giorno", fmtNum(new Set(valid.map((r) => r.date)).size ? sum("vehicles_out") / new Set(valid.map((r) => r.date)).size : 0, 1), "sum-vehicles"],
    ["Costo medio/km netto", kmTot ? `${fmtNum(kmCostNet / kmTot, 3)} €` : "—", "sum-cost-per-km"],
    ["Giorni / in attesa", `${rows.length} / ${pendingCount}`, "sum-days"],
  ];
  const exportXlsx = async () => {
    try {
      const params = { date_from: f.date_from || undefined, date_to: f.date_to || undefined, station_id: f.station_id === "all" ? undefined : f.station_id };
      const { data } = await api.get("/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fleetfuel_${current ? current.name + "_" : ""}${f.date_from || "inizio"}_${f.date_to || "oggi"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const overRate = (r) => r.reimbursement_rate != null && r.cost_per_km_net > r.reimbursement_rate;

  return (
    <div className="fade-up">
      <PageHeader title={current ? current.name : "Controllo dati"} subtitle={current ? "Dati e controllo della singola stazione." : "Verifica, approva, modifica o segnala i record di tutte le stazioni."}>
        <Button variant="outline" onClick={() => setShowBulk(true)} data-testid="bulk-reimb-button"><Receipt className="mr-2 h-4 w-4" /> Rimborso massivo</Button>
        <Button variant="outline" onClick={exportXlsx} data-testid="export-excel-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Esporta Excel</Button>
        <Button onClick={approveAll} disabled={!pendingCount} data-testid="approve-all-button"><Check className="mr-2 h-4 w-4" /> Approva tutti in attesa ({pendingCount})</Button>
      </PageHeader>

      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="station-tabs">
        {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((s) => {
          const active = f.station_id === s.id;
          return (
            <button key={s.id} onClick={() => selectStation(s.id)} data-testid={`station-tab-${s.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {s.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, s.id) }} />}
              {s.name}
            </button>
          );
        })}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="station-summary">
        {summary.map(([label, value, id, tone]) => (
          <div key={id} className="rounded-xl border border-slate-200 bg-white p-4" style={current ? { borderTop: `3px solid ${stationColor(stations, current.id)}` } : undefined}>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
            <p className={`font-mono-num mt-1 text-lg font-semibold ${tone || "text-slate-900"}`} data-testid={id}>{value}</p>
          </div>
        ))}
      </div>
      <p className="mb-6 text-xs text-slate-400">I totali escludono i record segnalati. Differenza netta = rimborso (netto IVA) − gasolio al netto dell'IVA 22%, calcolata sui soli giorni con rimborso inserito.</p>

      <div className="mb-6 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 lg:grid-cols-4">
        <div className="relative space-y-1"><Label className="text-xs">Dal</Label><button type="button" onClick={() => setF({ ...f, date_from: monthStartISO(), date_to: todayISO() })} className="absolute right-0 top-0 text-xs font-semibold leading-none text-sky-700 hover:underline" data-testid="admin-filter-month-reset">Mese in corso</button><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="admin-filter-date-start" /></div>
        <div className="space-y-1"><Label className="text-xs">Al</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="admin-filter-date-end" /></div>
        <div className="space-y-1"><Label className="text-xs">Stazione</Label>
          <Select value={f.station_id} onValueChange={selectStation}>
            <SelectTrigger data-testid="admin-filter-station-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le stazioni</SelectItem>
              {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Stato</Label>
          <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
            <SelectTrigger data-testid="admin-filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="Pending">In attesa</SelectItem>
              <SelectItem value="Approved">Approvato</SelectItem>
              <SelectItem value="Rejected">Segnalato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <AdminEntryCards rows={rows} stations={stations} overRate={overRate} actions={{ approve, setReimbursing, setEditing, setRejecting, remove }} />
      <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
        <div className="max-h-[calc(100vh-7rem)] overflow-auto">
          <table className="w-full text-sm" data-testid="admin-control-table">
            <thead className="sticky top-0 z-10 border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr>
                <S k="date">Data</S><S k="station_name">Stazione</S><R k="vehicles_out">Mezzi</R><R k="kilometers">KM</R>
                <R k="total_cost">Importo</R><R k="fuel_price_per_liter">€/L</R><R k="fuel_liters">Litri</R>
                <R k="km_per_liter">km/l</R><R k="cost_per_km_net">€/km netto</R><R k="reimbursed_km">Km Amazon</R><R k="reimbursement_amount">Rimborso</R><R k="difference">Diff. netta</R><S k="status">Stato</S><S k="created_by_name">Inserito da</S><th className="px-3 py-2 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sorted.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50" data-testid={`admin-row-${r.id}`}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{fmtDate(r.date)}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, r.station_id) }} />{r.station_name}</span>
                    {r.vehicle_plate && <span className="block text-xs text-slate-400">{r.vehicle_plate}</span>}
                  </td>
                  <td className="font-mono-num px-3 py-2 text-right" data-testid={`admin-vehicles-${r.id}`}>{r.vehicles_out ?? "—"}</td>
                  <td className="font-mono-num px-3 py-2 text-right">{fmtNum(r.kilometers, 1)}</td>
                  <td className="font-mono-num px-3 py-2 text-right font-semibold" data-testid={`admin-amount-${r.id}`}>{fmtEur(r.total_cost)}</td>
                  <td className="font-mono-num px-3 py-2 text-right">{fmtNum(r.fuel_price_per_liter, 3)}</td>
                  <td className="font-mono-num px-3 py-2 text-right text-slate-500">{fmtNum(r.fuel_liters, 2)}</td>
                  <td className="px-3 py-2 text-right"><span className={`font-mono-num rounded-md border px-2 py-0.5 text-xs font-semibold ${kmlTone(r.km_per_liter)}`}>{fmtNum(r.km_per_liter, 2)}</span></td>
                  <td className={`font-mono-num px-3 py-2 text-right ${overRate(r) ? "bg-rose-50 font-semibold text-rose-600" : ""}`} title={overRate(r) ? "Costo al km superiore alla tariffa di rimborso" : undefined} data-testid={`admin-ckm-${r.id}`}>{fmtNum(r.cost_per_km_net, 3)}</td>
                  <td className="font-mono-num px-3 py-2 text-right" data-testid={`admin-reimb-km-${r.id}`}>{r.reimbursed_km != null ? fmtNum(r.reimbursed_km, 1) : "—"}</td>
                  <td className="font-mono-num px-3 py-2 text-right" data-testid={`admin-reimb-amount-${r.id}`}>{r.reimbursement_amount != null ? fmtEur(r.reimbursement_amount) : "—"}{r.reimbursement_rate != null && <span className="block text-[11px] font-normal text-slate-400">{fmtNum(r.reimbursement_rate, 3)} €/km</span>}</td>
                  <td className={`font-mono-num px-3 py-2 text-right font-semibold ${diffTone(r.difference)}`} data-testid={`admin-diff-${r.id}`}>{r.difference != null ? fmtEur(r.difference) : "—"}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} testId={`admin-status-${r.id}`} />
                    {r.admin_notes && <p className="mt-1 max-w-[140px] truncate text-xs text-rose-600" title={r.admin_notes}>{r.admin_notes}</p>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{r.created_by_name}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" title="Approva" disabled={r.status === "Approved"} onClick={() => approve(r)} data-testid={`action-approve-entry-${r.id}`} className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Rimborso Amazon" onClick={() => setReimbursing(r)} data-testid={`action-reimb-entry-${r.id}`} className="text-blue-600 hover:bg-blue-50 hover:text-blue-700"><Receipt className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Modifica" onClick={() => setEditing(r)} data-testid={`action-edit-entry-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Segnala errore" onClick={() => setRejecting(r)} data-testid={`action-reject-entry-${r.id}`} className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"><Flag className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Elimina" onClick={() => remove(r)} data-testid={`action-delete-entry-${r.id}`} className="text-slate-400"><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={15} className="px-4 py-12 text-center text-slate-500" data-testid="admin-table-empty">Nessun record per i filtri selezionati.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <EditEntryDialog entry={editing} isAdmin onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      <RejectDialog entry={rejecting} onClose={() => setRejecting(null)} onSaved={() => { setRejecting(null); load(); }} />
      <BulkReimbursementDialog open={showBulk} stations={stations} initial={{ date_from: f.date_from, date_to: f.date_to, station_id: f.station_id }} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); load(); }} />
      <ReimbursementDialog entry={reimbursing} onClose={() => setReimbursing(null)} onSaved={() => { setReimbursing(null); load(); }} />
    </div>
  );
}
